import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Divider,
  Alert,
  AlertTitle,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';

// Future component imports
// import PriceVarianceScatterPlot from '../../components/dashboard/PriceVarianceScatterPlot';
// import UnitPriceComparisonChart from '../../components/dashboard/UnitPriceComparisonChart';
// import RegionalPriceComparisonChart from '../../components/dashboard/RegionalPriceComparisonChart';
// import PriceOutliersTable from '../../components/dashboard/PriceOutliersTable';

interface PriceVariance {
  category: string;
  item: string;
  supplier: string;
  region: string;
  contractedPrice: number;
  actualPrice: number;
  variance: number;
  percentageVariance: number;
  isOutlier: boolean;
}

interface RegionalComparison {
  item: string;
  category: string;
  regions: {
    region: string;
    averagePrice: number;
    transactionCount: number;
  }[];
  maxPriceDifferencePercent: number;
}

const PriceVariancesPage: React.FC = () => {
  const theme = useTheme();

  // Mock data for demonstration
  const [priceVariances, setPriceVariances] = useState<PriceVariance[]>([
    {
      category: 'IT Hardware',
      item: 'Laptop',
      supplier: 'TechVendor',
      region: 'North America',
      contractedPrice: 950,
      actualPrice: 1050,
      variance: 100,
      percentageVariance: 10.5,
      isOutlier: true
    },
    {
      category: 'IT Hardware',
      item: 'Desktop Computer',
      supplier: 'TechVendor',
      region: 'EMEA',
      contractedPrice: 750,
      actualPrice: 780,
      variance: 30,
      percentageVariance: 4.0,
      isOutlier: false
    },
    {
      category: 'Office Supplies',
      item: 'Paper (Box)',
      supplier: 'Office Depot',
      region: 'North America',
      contractedPrice: 25,
      actualPrice: 24,
      variance: -1,
      percentageVariance: -4.0,
      isOutlier: false
    },
    {
      category: 'Office Supplies',
      item: 'Paper (Box)',
      supplier: 'Staples',
      region: 'North America',
      contractedPrice: 25,
      actualPrice: 28,
      variance: 3,
      percentageVariance: 12.0,
      isOutlier: true
    },
    {
      category: 'Logistics',
      item: 'Air Freight (per kg)',
      supplier: 'Global Logistics',
      region: 'APAC',
      contractedPrice: 8.5,
      actualPrice: 9.8,
      variance: 1.3,
      percentageVariance: 15.3,
      isOutlier: true
    }
  ]);

  const [regionalComparisons, setRegionalComparisons] = useState<RegionalComparison[]>([
    {
      item: 'Laptop',
      category: 'IT Hardware',
      regions: [
        { region: 'North America', averagePrice: 1050, transactionCount: 235 },
        { region: 'EMEA', averagePrice: 1150, transactionCount: 182 },
        { region: 'APAC', averagePrice: 1220, transactionCount: 129 },
        { region: 'LATAM', averagePrice: 1350, transactionCount: 76 }
      ],
      maxPriceDifferencePercent: 28.6 // Difference between highest and lowest
    },
    {
      item: 'Paper (Box)',
      category: 'Office Supplies',
      regions: [
        { region: 'North America', averagePrice: 26, transactionCount: 312 },
        { region: 'EMEA', averagePrice: 29, transactionCount: 274 },
        { region: 'APAC', averagePrice: 27, transactionCount: 186 },
        { region: 'LATAM', averagePrice: 32, transactionCount: 94 }
      ],
      maxPriceDifferencePercent: 23.1
    }
  ]);

  // Business Rules Implementation
  const varianceThreshold = 10; // Flag contract vs. actual price difference > 10%
  const outlierThreshold = 1.5; // Standard deviations for outlier detection
  const regionalDispThreshold = 10; // Flag regional disparity > 10%

  // Contract vs Actual Price Analysis
  const highVarianceItems = priceVariances.filter(item => 
    Math.abs(item.percentageVariance) > varianceThreshold
  );

  // Outlier items 
  const outlierItems = priceVariances.filter(item => item.isOutlier);

  // Regional price disparities
  const highDisparityItems = regionalComparisons.filter(item => 
    item.maxPriceDifferencePercent > regionalDispThreshold
  );

  // Alerts based on business rules
  const contractPriceAlert = highVarianceItems.length > 0 && (
    <Alert severity="warning" sx={{ mt: 2 }}>
      <AlertTitle>Contract vs. Actual Price Deviation</AlertTitle>
      {highVarianceItems.map((item, index) => (
        <div key={index}>
          {item.item} ({item.region}): {item.percentageVariance > 0 ? '+' : ''}{item.percentageVariance.toFixed(1)}% 
          variance from contracted price. Potential off-contract spending detected.
        </div>
      ))}
    </Alert>
  );

  const outlierAlert = outlierItems.length > 0 && (
    <Alert severity="error" sx={{ mt: 2 }}>
      <AlertTitle>Price Outliers Detected</AlertTitle>
      {outlierItems.map((item, index) => (
        <div key={index}>
          {item.item} ({item.supplier}, {item.region}): Price of ${item.actualPrice} 
          is an outlier. Potential maverick spending or pricing error.
        </div>
      ))}
    </Alert>
  );

  const regionalDisparityAlert = highDisparityItems.length > 0 && (
    <Alert severity="info" sx={{ mt: 2 }}>
      <AlertTitle>Regional Price Disparities</AlertTitle>
      {highDisparityItems.map((item, index) => (
        <div key={index}>
          {item.item}: {item.maxPriceDifferencePercent.toFixed(1)}% price difference across regions. 
          Standardized pricing recommended.
        </div>
      ))}
    </Alert>
  );

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Price Variances Analysis
      </Typography>

      <Grid container spacing={3}>
        {/* Scatter Plot */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, height: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              Price Variance Scatter Plot
              {/* <PriceVarianceScatterPlot /> will be placed here */}
            </Typography>
          </Paper>
        </Grid>

        {/* Unit Price Comparison by Supplier */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '350px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              Unit Price Comparison by Supplier
              {/* <UnitPriceComparisonChart /> will be placed here */}
            </Typography>
          </Paper>
        </Grid>

        {/* Regional Price Comparison */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '350px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              Regional Price Comparison
              {/* <RegionalPriceComparisonChart /> will be placed here */}
            </Typography>
          </Paper>
        </Grid>

        {/* Price Variance Insights */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Price Variance Insights
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {contractPriceAlert}
              {outlierAlert}
              {regionalDisparityAlert}
            </CardContent>
          </Card>
        </Grid>

        {/* Sample Price Variances Table */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Sample Price Variances
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Item</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Supplier</TableCell>
                      <TableCell>Region</TableCell>
                      <TableCell align="right">Contracted Price</TableCell>
                      <TableCell align="right">Actual Price</TableCell>
                      <TableCell align="right">Variance %</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {priceVariances.map((row, index) => (
                      <TableRow 
                        key={index}
                        sx={{ 
                          backgroundColor: Math.abs(row.percentageVariance) > varianceThreshold 
                            ? 'rgba(255, 152, 0, 0.1)' // Highlight high variance rows
                            : 'inherit'
                        }}
                      >
                        <TableCell>{row.item}</TableCell>
                        <TableCell>{row.category}</TableCell>
                        <TableCell>{row.supplier}</TableCell>
                        <TableCell>{row.region}</TableCell>
                        <TableCell align="right">${row.contractedPrice.toFixed(2)}</TableCell>
                        <TableCell align="right">${row.actualPrice.toFixed(2)}</TableCell>
                        <TableCell 
                          align="right"
                          sx={{ 
                            color: row.percentageVariance > 0 
                              ? theme.palette.error.main 
                              : row.percentageVariance < 0 
                                ? theme.palette.success.main 
                                : 'inherit'
                          }}
                        >
                          {row.percentageVariance > 0 ? '+' : ''}{row.percentageVariance.toFixed(1)}%
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PriceVariancesPage; 